import {useDebounce, ApplicationController} from "stimulus-use";
import {Turbo} from '@hotwired/turbo-rails';
import {TomInput} from "tom-select/dist/cjs/types/core";

export default class extends ApplicationController {
  static values = {
    redirect: Boolean,
    turboDisable: Boolean,
    loadingOverlay: Boolean,
    submitOnMount: Boolean,
    resetOnSubmit: Boolean
  };
  declare readonly resetOnSubmitValue: boolean;
  declare readonly submitOnMountValue: boolean;
  declare readonly loadingOverlayValue: boolean;
  declare readonly turboDisableValue: boolean;
  declare readonly redirectValue: boolean;
  static debounces = ['debouncedSubmit'];
  static targets = [ 'submit', 'cancel', 'toggle' ];
  declare readonly cancelTarget: HTMLButtonElement;
  declare readonly hasCancelTarget: boolean;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly hasSubmitTarget: boolean;
  declare readonly toggleTargets: HTMLElement[];
  declare readonly hasToggleTarget: boolean;

  connect() {
    useDebounce(this, { wait: 500 });
    (this as any).dispatch('load');
    const form = this.element as HTMLFormElement;
    form.addEventListener('turbo:submit-end', this.submitEnd);
    form.addEventListener('turbo:submit-start', this.submitStart);
    if (this.submitOnMountValue) form.requestSubmit();
  }

  disconnect() {
    const form = this.element as HTMLFormElement;
    form.removeEventListener('turbo:submit-end', this.submitEnd);
    form.removeEventListener('turbo:submit-start', this.submitStart);
  }

  toggleActions = (disabled: boolean) => {
    if (this.hasSubmitTarget) {
      if (disabled) {
        this.submitTarget.setAttribute('disabled', 'disabled');
        this.submitTarget.querySelector('.icon:not(.spinner)')?.classList?.add('hidden');
        this.submitTarget.querySelector('.icon.spinner')?.classList?.remove('hidden');
      }
      else {
        this.submitTarget.removeAttribute('disabled');
        this.submitTarget.querySelector('.icon:not(.spinner)')?.classList?.remove('hidden');
        this.submitTarget.querySelector('.icon.spinner')?.classList?.add('hidden');
      }
    }
    if (this.hasCancelTarget) {
      if (disabled) this.cancelTarget.setAttribute('disabled', 'disabled');
      else this.cancelTarget.removeAttribute('disabled');
    }
  }

  submitStart = e => {
    this.toggleActions(true);
    if (this.loadingOverlayValue) document.getElementById('loading-overlay')?.classList?.remove('hidden');
    if (this.hasToggleTarget) this.toggleTargets.forEach(el => {
      const { submitStart } = el.dataset;
      if (submitStart === 'show') el.classList.remove('hidden');
      else if (submitStart === 'hide') el.classList.add('hidden');
    });
  }

  submitEnd = e => {
    this.toggleActions(false);
    if (this.loadingOverlayValue) document.getElementById('loading-overlay')?.classList?.add('hidden');
    if (this.hasToggleTarget) this.toggleTargets.forEach(el => {
      const { submitEnd } = el.dataset;
      if (submitEnd === 'show') el.classList.remove('hidden');
      else if (submitEnd === 'hide') el.classList.add('hidden');
    });
    const { success, fetchResponse } = e.detail,
        { response } = fetchResponse ?? {},
        { redirected, url } = response ?? {};
    if (success && this.resetOnSubmitValue) this.resetInputs();
    if (this.redirectValue) {
      if (!success || !redirected) return;
      Turbo.visit(url);
    }
  }

  debouncedSubmit = () => (this.element as HTMLFormElement).requestSubmit();

  submit = () => (this.element as HTMLFormElement).requestSubmit();

  resetInputs = () => {
    const form = this.element as HTMLFormElement;
    form.querySelectorAll('input:not([type="checkbox"]):not(.prevent-reset), textarea:not(.prevent-reset)').forEach((el: HTMLInputElement) => el.value = '');
    form.querySelectorAll('input[type=checkbox]:not(.prevent-reset)').forEach((el: HTMLInputElement) => el.checked = false);
    form.querySelectorAll('select:not(.prevent-reset)').forEach((el: HTMLSelectElement) => {
      if (el.classList.contains('tomselected')) (el as TomInput).tomselect?.clear();
      else Array.from(el.children).forEach((option: HTMLOptionElement) => option.selected = false);
    });
  }

  resetInput = e => {
    const inputs: string[] = JSON.parse(e.currentTarget.dataset.inputs);
    const form = this.element as HTMLFormElement;
    inputs.forEach(inputName => {
      Array.from(form.querySelectorAll(`[name="${inputName}"]`)).forEach((input: HTMLInputElement) => {
        const tag = input.tagName.toLowerCase(),
            type = input.getAttribute('type');
        if (tag === 'input') {
          if (type === 'checkbox') input.checked = false;
          else input.value = '';
        }
        else if (tag === 'select') input.value = '';
        else if (tag === 'textarea') input.value = '';
      });
    });
    this.submit();
  }

  reset = e => {
    e.preventDefault();
    this.resetInputs();
    this.submit();
  }

  resetSearch = e => {
    const searchInput = this.element.querySelector('#table-q') as HTMLInputElement;
    if (!searchInput) return;
    searchInput.value = '';
    this.submit();
  }
}
